import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { fetchHanArchives } from '../../../services/hanArchives.api'
import HanArchivesTabs, { HanArchivesHistoriaNav } from './HanArchivesTabs'
import HanArchivesHero from './HanArchivesHero'
import HanArchiveSection from './HanArchiveSection'
import '../../../styles/parchment-page.css'
import './HanArchives.css'

function parseHash() {
  const raw = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '')
  return raw || ''
}

export default function ViewHanArchives() {
  const { slug } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('historia')
  const [activeHistoriaSection, setActiveHistoriaSection] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const row = await fetchHanArchives(slug)
        if (!cancelled) setData(row)
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status ?? e?.status
          if (status === 404) {
            setError('Archivos no disponibles para este territorio.')
          } else {
            setError(e?.message || 'Error cargando los archivos')
          }
          setData(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug])

  const allTabs = useMemo(() => (Array.isArray(data?.tabs) ? data.tabs : []), [data])

  const activeTabData = useMemo(
    () => allTabs.find((t) => t.id === activeTab) || allTabs[0] || null,
    [allTabs, activeTab]
  )

  const historiaSections = useMemo(() => {
    if (activeTabData?.id !== 'historia') return []
    return (activeTabData.filledSections || []).filter((s) => s.hasContent)
  }, [activeTabData])

  useEffect(() => {
    if (!allTabs.length) return
    const hash = parseHash()
    const tabFromHash = allTabs.find((t) => t.id === hash)
    const sectionTab = allTabs.find((t) =>
      (t.filledSections || []).some((s) => s.id === hash)
    )
    if (tabFromHash) {
      setActiveTab(tabFromHash.id)
    } else if (sectionTab) {
      setActiveTab(sectionTab.id)
      if (sectionTab.id === 'historia') setActiveHistoriaSection(hash)
    } else {
      setActiveTab('historia')
    }
  }, [allTabs])

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId)
    try {
      window.history.replaceState(null, '', `#${tabId}`)
    } catch {
      window.location.hash = tabId
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const territoryUrl = `/han/${encodeURIComponent(slug || '')}`

  return (
    <div className="page parchment-scope han-archives-page">
      <div className="han-archives-page__inner">
        {loading && (
          <div className="han-archives-state">
            <Link to={territoryUrl} className="han-archives-page__back">
              ← Volver al Han
            </Link>
            <div className="han-archives-state__panel">
              <span className="han-archives-state__kanji" aria-hidden="true">
                卷
              </span>
              <p className="han-archives-state__text">Desplegando los archivos…</p>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="han-archives-state">
            <Link to={territoryUrl} className="han-archives-page__back">
              ← Volver al Han
            </Link>
            <div className="han-archives-state__panel han-archives-state__panel--error">
              <p className="han-archives-page__error">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <HanArchivesHero
              slug={slug}
              name={data.name}
              tagline={data.shortDescription}
              territoryUrl={territoryUrl}
              climate={data.climate}
              territoryStatus={data.territory_status}
              leaderName={data.leader_name}
              leaderTitle={data.leader_title}
            />

            {!data.hasArchives ? (
              <div className="han-archives-state">
                <div className="han-archives-state__panel">
                  <span className="han-archives-state__kanji" aria-hidden="true">
                    空
                  </span>
                  <p className="han-archives-state__text">
                    Aún no hay registros en los archivos de este Han. El staff puede añadirlos desde
                    el panel de administración.
                  </p>
                </div>
              </div>
            ) : (
              <div className="han-archives-body han-archives-body--tabs">
                <aside className="han-archives-sidebar">
                  <HanArchivesTabs
                    tabs={allTabs}
                    activeTab={activeTabData?.id}
                    onTabChange={handleTabChange}
                  />
                </aside>

                {activeTabData ? (
                  <main
                    className="han-archives-main han-archives-tabpanel"
                    role="tabpanel"
                    id={`han-tabpanel-${activeTabData.id}`}
                    aria-labelledby={`han-tab-${activeTabData.id}`}
                  >
                    {activeTabData.id === 'historia' ? (
                      <HanArchivesHistoriaNav
                        sections={historiaSections}
                        activeSection={activeHistoriaSection}
                        onSectionChange={setActiveHistoriaSection}
                      />
                    ) : null}

                    <div className="han-archives-tabpanel__sections">
                      {(activeTabData.filledSections || []).length > 0 ? (
                        activeTabData.filledSections.map((section) => (
                          <HanArchiveSection
                            key={section.id}
                            section={section}
                            isHistoriaPart={activeTabData.id === 'historia'}
                          />
                        ))
                      ) : (
                        <div className="han-archives-tabpanel__empty">
                          <span className="han-archives-tabpanel__empty-kanji" aria-hidden="true">
                            空
                          </span>
                          <p>Aún no hay registros en «{activeTabData.title}».</p>
                        </div>
                      )}
                    </div>
                  </main>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
